// frontend/src/features/auth/hooks/useTelegramAuth.ts
import { useCallback, useState } from "react";
import { useAuth, type ConsentData } from "@shared/contexts/auth-context";
import { useToast } from "@shared/helper_lib/hooks/use-toast";

// Define the Telegram user data structure based on Telegram's documentation
export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

// Make sure the Telegram object is available on the window
declare global {
  interface Window {
    Telegram: {
      Login: {
        auth: (
          options: { bot_id: string; request_access?: boolean },
          callback: (user: TelegramUser | false) => void
        ) => void;
      };
    };
  }
}

export function useTelegramAuth(consents?: ConsentData, openAuthModal?: (mode: "signin" | "signup") => void) {
  const { signInWithTelegram } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const onTelegramAuth = useCallback(
    async (user: TelegramUser) => {
      try {
        setIsLoading(true);
        await signInWithTelegram(user, consents);
      } catch (error) {
        console.error("Telegram auth failed:", error);

        if (typeof error === "object" && error !== null && "response" in error) {
          const response = (error as { response?: { data?: { message?: string } } }).response;
          const messageText = response?.data?.message;

          if (messageText === "Необходимо принять условия оферты и политику конфиденциальности") {
            openAuthModal?.("signup");

            toast({
              title: "Аккаунт не найден",
              description: "Пожалуйста, зарегистрируйтесь, приняв условия оферты и политику конфиденциальности",
              variant: "destructive"
            });
            setIsLoading(false);
            return;
          }
        }

        toast({
          title: "Ошибка авторизации",
          description: "Не удалось выполнить вход через Telegram.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    },
    [signInWithTelegram, toast, consents, openAuthModal]
  );

  const handleAuth = useCallback(() => {
    if (window.Telegram && window.Telegram.Login) {
      const botName = import.meta.env.VITE_TELEGRAM_BOT_NAME;

      if (!botName) {
        console.error("VITE_TELEGRAM_BOT_NAME is not set in .env");
        toast({
          title: "Ошибка конфигурации",
          description: "Авторизация через Telegram не настроена.",
          variant: "destructive"
        });
        return;
      }

      window.Telegram.Login.auth({ bot_id: botName, request_access: true }, data => {
        if (data) {
          onTelegramAuth(data);
        } else {
          // User cancelled the login
        }
      });
    } else {
      console.error("Telegram widget script is not loaded.");
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить виджет Telegram. Попробуйте обновить страницу.",
        variant: "destructive"
      });
    }
  }, [onTelegramAuth, toast]);

  return {
    isLoading,
    handleAuth
  };
}
